import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Check,
  CheckCircle2,
  ChevronRight,
  Database,
  Filter,
  Pencil,
  Plus,
  Search,
  Shield,
  Table2,
  Trash2,
  Users,
} from 'lucide-react';
import { SOURCE_META } from '../dataModelsConstants';
import type { AccessMember, CatalogTabId, DataModel, DataSource, ModelTable, Space } from '../dataModelsTypes';
import { formatRelative, initials, sourceById, sourceTypeLabel } from '../dataModelsUtils';
import { assetKind, sourcesForSpace } from './SpaceOverview';
import CatalogGovernancePanel, { summarizePolicies } from './CatalogGovernancePanel';
import { resolveGovernance, useGovernance } from './governanceStore';
import DataModelsAuditPanel from './DataModelsAuditPanel';
import QueryLogsPanel from './QueryLogsPanel';
import { DialogShell, ModelIconTile, SourceIconTile, StatusPill, ViewRowButton, ghostBtn, primaryBtn, surfaces } from './dataModelsUi';

type AssetType = 'all' | 'Table' | 'View' | 'Collection' | 'Other';
type ProtectStatus = 'all' | 'Protected' | 'Partially Protected' | 'Not Protected';

const TABS: { id: CatalogTabId; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'assets', label: 'Assets' },
  { id: 'policies', label: 'Governance' },
  { id: 'access', label: 'Access' },
  { id: 'activity', label: 'Activity' },
  { id: 'queries', label: 'Logs' },
];

const PAGE_SIZE = 8;

export default function CatalogWorkspace({
  isLight,
  space,
  model,
  tables,
  sources,
  members,
  onBackToSpace,
  onEdit,
  onAddAsset,
  onRemoveAsset,
  assetBusy = false,
  onSelectTable,
  tab,
  onTab,
}: {
  isLight: boolean;
  space: Space;
  model: DataModel;
  tables: ModelTable[];
  sources: DataSource[];
  members: AccessMember[];
  onBackToSpace: () => void;
  onEdit: () => void;
  onAddAsset: () => void;
  onRemoveAsset?: (tableId: string) => Promise<void>;
  assetBusy?: boolean;
  onSelectTable: (tableId: string) => void;
  tab: CatalogTabId;
  onTab: (tab: CatalogTabId) => void;
}) {
  const ui = surfaces(isLight);
  const muted = ui.muted;
  const [pendingDelete, setPendingDelete] = useState<ModelTable | null>(null);
  const { policies: storedPolicies, assets } = useGovernance();
  const policy = useMemo(
    () => summarizePolicies(resolveGovernance(storedPolicies, { level: 'model', spaceId: space.id, modelId: model.id, inherit: true }, assets)),
    [storedPolicies, assets, space.id, model.id],
  );
  const catalogSources = useMemo(() => sourcesForSpace(tables, sources), [tables, sources]);
  const rows = useMemo(() => tables.map((table) => decorateAsset(table, sources)), [tables, sources]);
  const protectedCount = rows.filter((row) => row.protection !== 'Not Protected').length;
  const coverage = rows.length ? Math.round((protectedCount / rows.length) * 100) : 0;
  const typeCounts = countTypes(rows);
  const roleCounts = countRoles(members);

  const openPolicies = () => onTab('policies');

  return (
    <section className={`flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden ${ui.tabBar}`}>
      <div className="shrink-0 px-5 pb-2 pt-3">
        <div className={`mb-2 flex flex-wrap items-center gap-1 text-xs ${muted}`}>
          <button type="button" onClick={onBackToSpace} className="hover:underline">WorkSpace</button>
          <ChevronRight size={12} />
          <button type="button" onClick={onBackToSpace} className="hover:underline">{space.name}</button>
          <ChevronRight size={12} />
          <span className={isLight ? 'font-medium text-[#1C1C1C]' : 'font-medium text-white'}>{model.name}</span>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2">
            <ModelIconTile isLight={isLight} />
            <h2 className={`truncate text-lg font-semibold ${isLight ? 'text-[#1C1C1C]' : 'text-white'}`}>{model.name}</h2>
            <StatusPill status={model.status === 'published' ? 'active' : model.status} isLight={isLight} />
          </div>
          <div className="flex items-center gap-2">
            <button type="button" className={ghostBtn(isLight)} onClick={onEdit}>
              <Pencil size={14} />
              Edit
            </button>
            <button type="button" className={primaryBtn()} onClick={onAddAsset}>
              <Plus size={14} />
              Add assets
            </button>
          </div>
        </div>
      </div>

      <div className={`flex shrink-0 gap-1 overflow-x-auto border-b px-3 ${ui.border} ${ui.tabBar}`}>
        {TABS.map((item) => {
          const active = tab === item.id;
          return (
            <button key={item.id} type="button" onClick={() => onTab(item.id)} className={`relative shrink-0 px-3 py-2 text-[13px] ${active ? `font-semibold ${ui.accentText}` : muted}`}>
              {item.label}
              {active ? <span className={`absolute inset-x-2 bottom-0 h-0.5 ${ui.accent}`} /> : null}
            </button>
          );
        })}
      </div>

      <div className={`relative min-h-0 flex-1 ${ui.body}`}>
        <div className="absolute inset-0 overflow-y-auto overflow-x-hidden px-5 py-4">
          {tab === 'overview' ? (
            <Overview
              isLight={isLight}
              rows={rows}
              sources={catalogSources}
              tables={tables}
              policy={policy}
              coverage={coverage}
              protectedCount={protectedCount}
              typeCounts={typeCounts}
              members={members}
              roleCounts={roleCounts}
              onAddAsset={onAddAsset}
              onOpenPolicies={openPolicies}
              onSelectTable={onSelectTable}
            />
          ) : null}
          {tab === 'assets' ? (
            <AssetBrowser
              isLight={isLight}
              rows={rows}
              onAddAsset={onAddAsset}
              onSelectTable={onSelectTable}
              onRemove={onRemoveAsset ? (table) => setPendingDelete(table) : undefined}
            />
          ) : null}
          {tab === 'policies' ? <CatalogGovernancePanel isLight={isLight} level="model" space={space} model={model} /> : null}
          {tab === 'access' ? <AccessList isLight={isLight} members={members} /> : null}
          {tab === 'activity' ? (
            <DataModelsAuditPanel
              isLight={isLight}
              workspaceId={space.id}
              catalogId={model.id}
              streams={['catalog', 'asset', 'policy']}
            />
          ) : null}
          {tab === 'queries' ? (
            <QueryLogsPanel isLight={isLight} workspaceId={space.id} catalogId={model.id} />
          ) : null}
        </div>
      </div>
      {pendingDelete && onRemoveAsset ? (
        <DialogShell
          title="Remove asset"
          subtitle={`${pendingDelete.name} will be removed from ${model.name}.`}
          isLight={isLight}
          onClose={() => setPendingDelete(null)}
          footer={
            <>
              <button type="button" className={ghostBtn(isLight)} onClick={() => setPendingDelete(null)}>Cancel</button>
              <button
                type="button"
                className={primaryBtn()}
                disabled={assetBusy}
                onClick={() => {
                  const tableId = pendingDelete.id;
                  void onRemoveAsset(tableId).then(() => setPendingDelete(null));
                }}
              >
                Remove
              </button>
            </>
          }
        >
          <p className={`text-sm ${isLight ? 'text-[#3C4048]' : 'text-slate-300'}`}>
            This removes the asset from this catalog only. The source connection is not deleted.
          </p>
        </DialogShell>
      ) : null}
    </section>
  );
}

function Overview({
  isLight,
  rows,
  sources,
  tables,
  policy,
  coverage,
  protectedCount,
  typeCounts,
  members,
  roleCounts,
  onAddAsset,
  onOpenPolicies,
  onSelectTable,
}: {
  isLight: boolean;
  rows: AssetRow[];
  sources: DataSource[];
  tables: ModelTable[];
  policy: ReturnType<typeof summarizePolicies>;
  coverage: number;
  protectedCount: number;
  typeCounts: Record<string, number>;
  members: AccessMember[];
  roleCounts: Array<{ dot: string; text: string }>;
  onAddAsset: () => void;
  onOpenPolicies: () => void;
  onSelectTable: (tableId: string) => void;
}) {
  const maxKind = Math.max(policy.filters, policy.masks, policy.access, policy.classes, 1);
  return (
    <div className="space-y-4">
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        <Kpi isLight={isLight} icon={Database} tone={isLight ? 'bg-sky-100 text-sky-700' : 'bg-sky-500/20 text-sky-200'} label="Total Assets" value={String(rows.length)} footer={<Dots isLight={isLight} items={[{ dot: 'bg-blue-500', text: `${typeCounts.Table ?? 0} Tables` }, { dot: 'bg-violet-500', text: `${typeCounts.View ?? 0} Views` }, { dot: 'bg-slate-400', text: `${(typeCounts.Collection ?? 0) + (typeCounts.Other ?? 0)} Others` }]} />} />
        <Kpi isLight={isLight} icon={Shield} tone={isLight ? 'bg-emerald-100 text-emerald-700' : 'bg-emerald-500/20 text-emerald-200'} label="Policies Applied" value={String(policy.total)} footer={<Dots isLight={isLight} items={[{ dot: 'bg-blue-500', text: `${policy.inherited} Inherited` }, { dot: 'bg-violet-500', text: `${policy.catalog} Model` }, { dot: 'bg-orange-400', text: `${policy.overrides} Overrides` }]} />} />
        <Kpi isLight={isLight} icon={CheckCircle2} tone={isLight ? 'bg-emerald-100 text-emerald-700' : 'bg-emerald-500/20 text-emerald-200'} label="Policy Coverage" value={`${coverage}%`} footer={<div><div className={`h-1.5 overflow-hidden rounded-full ${isLight ? 'bg-slate-100' : 'bg-white/10'}`}><span className={`block h-full rounded-full ${isLight ? 'bg-[#1B6CA8]' : 'bg-sky-400'}`} style={{ width: `${coverage}%` }} /></div><p className={`mt-2 text-[11px] ${isLight ? 'text-slate-500' : 'text-slate-400'}`}>{protectedCount} of {rows.length} assets have policies</p></div>} />
        <Kpi isLight={isLight} icon={Users} tone={isLight ? 'bg-indigo-100 text-indigo-700' : 'bg-indigo-500/20 text-indigo-200'} label="Members" value={String(members.length)} footer={<Dots isLight={isLight} items={roleCounts} />} />
      </div>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_300px]">
        <section className={`rounded-2xl border ${surfaces(isLight).card}`}>
          <div className="flex items-center justify-between px-4 py-3">
            <h3 className={`text-sm font-semibold ${isLight ? 'text-slate-900' : 'text-white'}`}>Assets ({rows.length})</h3>
          </div>
          <AssetBrowser isLight={isLight} rows={rows} onAddAsset={onAddAsset} onSelectTable={onSelectTable} embedded />
        </section>
        <div className="space-y-4">
          <section className={`rounded-2xl border p-4 ${surfaces(isLight).card}`}>
            <div className="mb-3 flex items-center justify-between">
              <h3 className={`text-sm font-semibold ${isLight ? 'text-slate-900' : 'text-white'}`}>Policy Summary</h3>
              <button type="button" onClick={onOpenPolicies} className={`text-xs ${isLight ? 'text-[#1B6CA8]' : 'text-sky-300'}`}>View all</button>
            </div>
            <SummaryBar label="Row Filters" value={policy.filters} max={maxKind} bar="bg-blue-500" isLight={isLight} />
            <SummaryBar label="Column Masks" value={policy.masks} max={maxKind} bar="bg-violet-500" isLight={isLight} />
            <SummaryBar label="Access Control" value={policy.access} max={maxKind} bar="bg-orange-400" isLight={isLight} />
            <SummaryBar label="Classification Rules" value={policy.classes} max={maxKind} bar="bg-emerald-500" isLight={isLight} />
          </section>
          <section className={`rounded-2xl border ${surfaces(isLight).card}`}>
            <div className="flex items-center justify-between px-4 py-3">
              <h3 className={`text-sm font-semibold ${isLight ? 'text-slate-900' : 'text-white'}`}>Data Sources ({sources.length})</h3>
              <span className={`inline-flex items-center gap-1 text-xs ${isLight ? 'text-[#6B7280]' : 'text-slate-400'}`}><Plus size={12} /> Connect source</span>
            </div>
            <table className="min-w-full text-[13px]">
              <thead className={surfaces(isLight).tableHead}>
                <tr className="text-left text-[11px] font-semibold uppercase tracking-wider">
                  <th className="px-3 py-2">Name</th>
                  <th className="px-3 py-2">Type</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Assets</th>
                </tr>
              </thead>
              <tbody>
                {sources.map((source) => (
                  <tr key={source.id} className={`border-t ${surfaces(isLight).border}`}>
                    <td className="px-3 py-2">
                      <span className="inline-flex items-center gap-2">
                        <SourceIconTile kind={source.kind} isLight={isLight} box="h-6 w-6" />
                        <span className={`font-medium ${isLight ? 'text-slate-900' : 'text-white'}`}>{SOURCE_META[source.kind].label}</span>
                      </span>
                    </td>
                    <td className={`px-3 py-2 ${isLight ? 'text-slate-500' : 'text-slate-400'}`}>{sourceTypeLabel(source.kind, source.environment)}</td>
                    <td className={`px-3 py-2 text-xs ${isLight ? 'text-[#3C4048]' : 'text-slate-300'}`}>Connected</td>
                    <td className="px-3 py-2 tabular-nums">{tables.filter((table) => table.sourceId === source.id).length}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        </div>
      </div>
    </div>
  );
}

function AssetBrowser({
  isLight,
  rows,
  onAddAsset,
  onSelectTable,
  onRemove,
  embedded,
}: {
  isLight: boolean;
  rows: AssetRow[];
  onAddAsset: () => void;
  onSelectTable: (tableId: string) => void;
  onRemove?: (table: ModelTable) => void;
  embedded?: boolean;
}) {
  const ui = surfaces(isLight);
  const muted = ui.muted;
  const [query, setQuery] = useState('');
  const [type, setType] = useState<AssetType>('all');
  const [sourceId, setSourceId] = useState('all');
  const [status, setStatus] = useState<ProtectStatus>('all');
  const [filterOpen, setFilterOpen] = useState(false);
  const filterButtonRef = useRef<HTMLButtonElement>(null);
  const [page, setPage] = useState(1);
  const sourceChoices = useMemo(() => {
    const seen = new Map<string, DataSource>();
    rows.forEach((row) => {
      if (row.source?.id && !seen.has(row.source.id)) seen.set(row.source.id, row.source);
    });
    return Array.from(seen.values()).sort((left, right) => left.name.localeCompare(right.name));
  }, [rows]);
  const filtered = rows.filter((row) => {
    if (type !== 'all' && row.type !== type) return false;
    if (sourceId !== 'all' && row.source?.id !== sourceId) return false;
    if (status !== 'all' && row.protection !== status) return false;
    const source = row.source;
    const haystack = `${row.table.name} ${row.type} ${source?.name ?? ''} ${sourceTypeName(source)}`.toLowerCase();
    return haystack.includes(query.trim().toLowerCase());
  });
  const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pages);
  const slice = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const filterLabels = [
    type !== 'all' ? type : '',
    sourceId !== 'all' ? sourceChoices.find((source) => source.id === sourceId)?.name ?? '' : '',
    status !== 'all' ? status : '',
  ].filter(Boolean);
  const filterLabel = filterLabels.length ? `Filter · ${filterLabels.join(' · ')}` : 'Filter';

  return (
    <div className={embedded ? '' : `rounded-2xl border ${ui.card}`}>
      <div className="flex flex-wrap items-center gap-2 px-4 py-3">
        <div className="relative min-w-[180px] flex-1">
          <Search size={14} className={`absolute left-3 top-1/2 -translate-y-1/2 ${muted}`} />
          <input value={query} onChange={(event) => { setQuery(event.target.value); setPage(1); }} placeholder="Search assets..." className={isLight ? 'h-9 w-full rounded-lg border border-slate-200 bg-white py-0 pl-9 pr-3 text-sm focus:border-indigo-400 focus:outline-none' : 'h-9 w-full rounded-lg border border-indigo-400/20 bg-black py-0 pl-9 pr-3 text-sm text-slate-100 focus:border-indigo-400 focus:outline-none'} />
        </div>
        {embedded ? (
          <>
            <select value={type} onChange={(event) => { setType(event.target.value as AssetType); setPage(1); }} className={selectClass(isLight)}>
              <option value="all">All Types</option>
              <option value="Table">Table</option>
              <option value="View">View</option>
              <option value="Collection">Collection</option>
              <option value="Other">Other</option>
            </select>
            <select value={status} onChange={(event) => { setStatus(event.target.value as ProtectStatus); setPage(1); }} className={selectClass(isLight)}>
              <option value="all">All Status</option>
              <option value="Protected">Protected</option>
              <option value="Partially Protected">Partially Protected</option>
              <option value="Not Protected">Not Protected</option>
            </select>
          </>
        ) : (
          <>
            <button
              ref={filterButtonRef}
              type="button"
              className={ghostBtn(isLight)}
              aria-expanded={filterOpen}
              aria-haspopup="listbox"
              onClick={() => setFilterOpen((open) => !open)}
            >
              <Filter size={14} />
              {filterLabel}
            </button>
            <AssetFilterPopover
              open={filterOpen}
              anchor={filterButtonRef.current}
              isLight={isLight}
              type={type}
              sourceId={sourceId}
              status={status}
              sourceChoices={sourceChoices}
              onClose={() => setFilterOpen(false)}
              onType={(value) => {
                setType(value);
                setPage(1);
              }}
              onSource={(value) => {
                setSourceId(value);
                setPage(1);
              }}
              onStatus={(value) => {
                setStatus(value);
                setPage(1);
              }}
            />
          </>
        )}
        <button type="button" className={primaryBtn()} onClick={onAddAsset}><Plus size={14} /> Add Asset</button>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-[13px]">
          <thead className={ui.tableHead}>
            <tr className="text-left text-[11px] font-semibold uppercase tracking-wider">
              <th className="px-3 py-2.5">Name</th>
              <th className="px-3 py-2.5">Type</th>
              <th className="px-3 py-2.5">Data Source</th>
              <th className="px-3 py-2.5">Policies</th>
              <th className="px-3 py-2.5">Status</th>
              <th className="px-3 py-2.5">Last Updated</th>
              <th className="w-16 px-3 py-2.5" />
            </tr>
          </thead>
          <tbody>
            {slice.map((row) => {
              const source = row.source;
              return (
                <tr key={row.table.id} className={`border-t ${ui.border} ${ui.row}`}>
                  <td className="px-3 py-2.5">
                    <span className="inline-flex items-center gap-2">
                      <span className={`inline-flex h-6 w-6 items-center justify-center rounded-md ${isLight ? 'bg-[#F2F4F7] text-[#4B5563]' : 'bg-white/10 text-slate-300'}`}><Table2 size={13} /></span>
                      <span className={`font-mono font-medium ${isLight ? 'text-slate-900' : 'text-white'}`}>{row.table.name}</span>
                    </span>
                  </td>
                  <td className={`px-3 py-2.5 ${muted}`}>{row.type}</td>
                  <td className="px-3 py-2.5">
                    {source ? (
                      <span className="inline-flex items-center gap-1.5">
                        <SourceIconTile kind={source.kind} isLight={isLight} box="h-6 w-6" />
                        <span className="min-w-0">
                          <span className={`block truncate font-medium ${isLight ? 'text-slate-900' : 'text-white'}`}>{source.name}</span>
                          <span className={`block truncate text-[11px] ${muted}`}>{sourceTypeName(source)}</span>
                        </span>
                      </span>
                    ) : '—'}
                  </td>
                  <td className="px-3 py-2.5 tabular-nums">{row.policies}</td>
                  <td className="px-3 py-2.5"><ProtectPill status={row.protection} isLight={isLight} /></td>
                  <td className={`px-3 py-2.5 ${muted}`}>{formatRelative(row.table.updatedAt)}</td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center justify-end gap-0.5">
                      <ViewRowButton isLight={isLight} label={`View ${row.table.name}`} onClick={() => onSelectTable(row.table.id)} />
                      {onRemove ? (
                        <button
                          type="button"
                          className={`inline-flex h-7 w-7 items-center justify-center rounded-md ${
                            isLight ? 'text-[#5C6570] hover:bg-[#F2F4F7]' : 'text-slate-300 hover:bg-white/10'
                          }`}
                          title="Remove asset"
                          aria-label={`Remove ${row.table.name}`}
                          onClick={() => onRemove(row.table)}
                        >
                          <Trash2 size={14} />
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              );
            })}
            {!slice.length ? <tr><td colSpan={7} className={`px-3 py-8 text-center text-sm ${muted}`}>No assets match this filter.</td></tr> : null}
          </tbody>
        </table>
      </div>
      <div className={`flex items-center justify-between px-4 py-3 text-xs ${muted}`}>
        <span>Showing {filtered.length ? (safePage - 1) * PAGE_SIZE + 1 : 0}-{Math.min(safePage * PAGE_SIZE, filtered.length)} of {filtered.length} assets</span>
        <div className="flex items-center gap-1">
          {Array.from({ length: pages }, (_, index) => index + 1).slice(0, 5).map((number) => (
            <button key={number} type="button" onClick={() => setPage(number)} className={`h-7 min-w-7 rounded-md px-2 ${number === safePage ? 'bg-[#1B6CA8] text-white' : isLight ? 'hover:bg-[#F4F6F8]' : 'hover:bg-white/10'}`}>{number}</button>
          ))}
        </div>
      </div>
    </div>
  );
}

function AccessList({ isLight, members }: { isLight: boolean; members: AccessMember[] }) {
  const ui = surfaces(isLight);
  if (!members.length) return <p className={`py-8 text-sm ${ui.muted}`}>No users on this model.</p>;
  return (
    <div className={`overflow-hidden rounded-2xl border ${ui.card}`}>
      {members.map((member) => (
        <div key={member.id} className={`flex items-center justify-between gap-3 border-b px-4 py-3 last:border-b-0 ${isLight ? 'border-indigo-50' : 'border-indigo-400/10'}`}>
          <div className="flex items-center gap-3">
            <span className={`inline-flex h-7 w-7 items-center justify-center rounded-md text-[11px] font-medium ${isLight ? 'bg-[#F2F4F7] text-[#3C4048]' : 'bg-white/10 text-slate-200'}`}>{initials(member.name)}</span>
            <div>
              <p className={`text-sm font-medium ${isLight ? 'text-slate-900' : 'text-white'}`}>{member.name}</p>
              <p className={`text-xs ${ui.muted}`}>{member.email}</p>
            </div>
          </div>
          <span className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${isLight ? 'bg-[#F2F4F7] text-[#3C4048]' : 'bg-white/10 text-slate-200'}`}>{member.role}</span>
        </div>
      ))}
    </div>
  );
}


function Kpi({ isLight, icon: Icon, label, value, footer }: { isLight: boolean; icon: React.ComponentType<{ size?: number }>; tone: string; label: string; value: string; footer: React.ReactNode }) {
  return (
    <article className={`rounded-2xl border p-4 ${surfaces(isLight).card}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className={`text-xs ${isLight ? 'text-slate-500' : 'text-slate-400'}`}>{label}</p>
          <p className={`mt-1 text-2xl font-semibold tabular-nums ${isLight ? 'text-slate-900' : 'text-white'}`}>{value}</p>
        </div>
        <span className={`inline-flex h-8 w-8 items-center justify-center rounded-md ${isLight ? 'bg-[#F2F4F7] text-[#4B5563]' : 'bg-white/10 text-slate-300'}`}><Icon size={15} /></span>
      </div>
      <div className="mt-3">{footer}</div>
    </article>
  );
}

function Dots({ isLight, items }: { isLight: boolean; items: Array<{ dot: string; text: string }> }) {
  return (
    <p className={`flex flex-wrap gap-x-3 gap-y-1 text-[11px] ${isLight ? 'text-slate-500' : 'text-slate-400'}`}>
      {items.map((item) => (
        <span key={item.text} className="inline-flex items-center gap-1.5"><span className={`h-1.5 w-1.5 rounded-full ${isLight ? 'bg-[#9AA1AB]' : 'bg-slate-500'}`} />{item.text}</span>
      ))}
    </p>
  );
}

function SummaryBar({ label, value, max, bar, isLight }: { label: string; value: number; max: number; bar: string; isLight: boolean }) {
  return (
    <div className="mb-2.5 flex items-center gap-3 text-xs">
      <span className={`w-28 shrink-0 ${isLight ? 'text-slate-600' : 'text-slate-300'}`}>{label}</span>
      <span className="w-4 tabular-nums">{value}</span>
      <span className={`h-1.5 flex-1 overflow-hidden rounded-full ${isLight ? 'bg-slate-100' : 'bg-white/10'}`}>
        <span className={`block h-full rounded-full ${isLight ? 'bg-[#1B6CA8]' : 'bg-sky-400'}`} style={{ width: `${(value / max) * 100}%` }} />
      </span>
    </div>
  );
}

function sourceTypeName(source?: DataSource): string {
  if (!source) return '';
  return source.connectionType || SOURCE_META[source.kind]?.label || source.kind;
}

function FilterHeading({ isLight, children }: { isLight: boolean; children: React.ReactNode }) {
  return (
    <p className={`px-3 pb-0.5 pt-2 text-[10px] font-semibold uppercase tracking-wide ${isLight ? 'text-[#6B7280]' : 'text-slate-500'}`}>
      {children}
    </p>
  );
}

function FilterChoice({
  isLight,
  active,
  onClick,
  children,
}: {
  isLight: boolean;
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-[13px] ${
        isLight ? 'text-[#1C1C1C] hover:bg-[#F3F7FB]' : 'text-slate-200 hover:bg-white/5'
      }`}
    >
      <span className="inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center">
        {active ? <Check size={12} /> : null}
      </span>
      <span className="min-w-0 truncate">{children}</span>
    </button>
  );
}

function AssetFilterPopover({
  open,
  anchor,
  isLight,
  type,
  sourceId,
  status,
  sourceChoices,
  onClose,
  onType,
  onSource,
  onStatus,
}: {
  open: boolean;
  anchor: HTMLElement | null;
  isLight: boolean;
  type: AssetType;
  sourceId: string;
  status: ProtectStatus;
  sourceChoices: DataSource[];
  onClose: () => void;
  onType: (value: AssetType) => void;
  onSource: (value: string) => void;
  onStatus: (value: ProtectStatus) => void;
}) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState({ top: 0, left: 0 });

  useLayoutEffect(() => {
    if (!open || !anchor) return;
    const place = () => {
      const rect = anchor.getBoundingClientRect();
      const width = 220;
      const left = Math.min(Math.max(8, rect.right - width), window.innerWidth - width - 8);
      setCoords({ top: rect.bottom + 4, left });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, anchor]);

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: MouseEvent) => {
      const target = event.target as Node;
      if (anchor?.contains(target) || menuRef.current?.contains(target)) return;
      onClose();
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [anchor, onClose, open]);

  if (!open || !anchor) return null;

  return createPortal(
    <div
      ref={menuRef}
      role="listbox"
      style={{ top: coords.top, left: coords.left }}
      className={`fixed z-[240] max-h-[min(24rem,calc(100vh-6rem))] w-[220px] overflow-y-auto rounded-md border py-1 shadow-md ${
        isLight ? 'border-[#E6E8EB] bg-white shadow-slate-200/80' : 'border-white/10 bg-black'
      }`}
    >
      <FilterHeading isLight={isLight}>Type</FilterHeading>
      {(['all', 'Table', 'View', 'Collection', 'Other'] as const).map((value) => (
        <FilterChoice key={`type-${value}`} isLight={isLight} active={type === value} onClick={() => onType(value)}>
          {value === 'all' ? 'All types' : value}
        </FilterChoice>
      ))}
      <FilterHeading isLight={isLight}>Source</FilterHeading>
      <FilterChoice isLight={isLight} active={sourceId === 'all'} onClick={() => onSource('all')}>
        All sources
      </FilterChoice>
      {sourceChoices.map((source) => (
        <FilterChoice key={source.id} isLight={isLight} active={sourceId === source.id} onClick={() => onSource(source.id)}>
          {source.name}
        </FilterChoice>
      ))}
      <FilterHeading isLight={isLight}>Status</FilterHeading>
      {(['all', 'Protected', 'Partially Protected', 'Not Protected'] as const).map((value) => (
        <FilterChoice key={`status-${value}`} isLight={isLight} active={status === value} onClick={() => onStatus(value)}>
          {value === 'all' ? 'All statuses' : value}
        </FilterChoice>
      ))}
    </div>,
    document.body,
  );
}

function ProtectPill({ status, isLight }: { status: AssetRow['protection']; isLight: boolean }) {
  return <span className={`inline-flex rounded px-1.5 py-0.5 text-[11px] font-medium ${isLight ? 'bg-[#F2F4F7] text-[#3C4048]' : 'bg-white/10 text-slate-200'}`}>{status}</span>;
}

interface AssetRow {
  table: ModelTable;
  source?: DataSource;
  type: 'Table' | 'View' | 'Collection' | 'Other';
  policies: number;
  protection: 'Protected' | 'Partially Protected' | 'Not Protected';
}

function decorateAsset(table: ModelTable, sources: DataSource[]): AssetRow {
  const kind = assetKind(table, sources);
  const type = kind === 'Tables' ? 'Table' : kind === 'Views' ? 'View' : kind === 'Collections' ? 'Collection' : 'Other';
  const n = hash(table.id);
  const protection = n % 10 < 6 ? 'Protected' : n % 10 < 8 ? 'Partially Protected' : 'Not Protected';
  return {
    table,
    source: sourceById(sources, table.sourceId),
    type,
    policies: protection === 'Not Protected' ? 0 : (n % 5) + 1,
    protection,
  };
}

function countTypes(rows: AssetRow[]) {
  return rows.reduce<Record<string, number>>((acc, row) => {
    acc[row.type] = (acc[row.type] ?? 0) + 1;
    return acc;
  }, {});
}

function countRoles(members: AccessMember[]) {
  const owners = members.filter((member) => /owner/i.test(member.role)).length;
  const editors = members.filter((member) => /editor|admin/i.test(member.role)).length;
  const viewers = Math.max(0, members.length - owners - editors);
  return [
    { dot: 'bg-blue-500', text: `${owners} Owners` },
    { dot: 'bg-violet-500', text: `${editors} Editors` },
    { dot: 'bg-slate-400', text: `${viewers} Viewers` },
  ];
}

function hash(value: string): number {
  return Array.from(value).reduce((sum, char) => sum + char.charCodeAt(0), 0);
}

function selectClass(isLight: boolean): string {
  return isLight
    ? 'h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700'
    : 'h-9 rounded-lg border border-indigo-400/20 bg-black px-3 text-sm text-slate-200';
}
